import { useMutation } from "@apollo/client";
import { Button } from "components/ui/v2/button";
import { useDrawer } from "context/drawer/drawer.provider";
import { gqlMessage } from "gql";
import { ICreateMessageInput } from "gql/Message/mutations";
import useTranslation from "next-translate/useTranslation";
import Image from "next/legacy/image";
import toast from "react-hot-toast";
import { EMessageType, IMessage } from "types/Message.type";
import { ISkill } from "types/Skills.type";
import useSound from "use-sound";
import { getQueryOperator } from "utils/helpers";

interface IRequestEndorsementProps {
  skill: ISkill;
  onClose?: () => void;
}

const RequestEndorsement = ({ skill, onClose }: IRequestEndorsementProps) => {
  const { t } = useTranslation("common");
  const { closeDrawer } = useDrawer();
  const [playComplete] = useSound("/sounds/complete.mp3", { volume: 0.25 });

  const [createMessage, { loading: createMessageLoading }] = useMutation<
    { createMessage: IMessage },
    {
      createMessageInput: ICreateMessageInput;
    }
  >(gqlMessage.mutations.CREATE_MESSAGE, {
    awaitRefetchQueries: true,
    refetchQueries: [getQueryOperator(gqlMessage.queries.GET_MESSAGES)],
    onCompleted: () => {
      toast.success(t("Solicitud de validación enviada correctamente"));
      playComplete();
      (onClose ?? closeDrawer)();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const handleRequestEndorsement = async () => {
    await createMessage({
      variables: {
        createMessageInput: {
          type: EMessageType.ENDORSEMENT,
          text: `Hola, me gustaría validar mi habilidad ${skill?.name}`,
          skill: skill?.id
        }
      }
    });
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-6 text-center">
      <Image
        src="/svg/request-endorsement.svg"
        width={220}
        height={150}
        alt="request-endorsement"
      />
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
        {t("Quieres Validar tu Habilidad")}?
      </h2>
      <span className="text-2xl font-bold capitalize text-indigo-600 dark:text-indigo-300">
        {skill?.name}
      </span>
      <p className="text-sm text-slate-500 dark:text-zinc-400">
        {t("Solicita una validación de esta habilidad")}
      </p>
      <div className="mt-2 w-full">
        <Button
          variant="primary"
          width="full"
          size="lg"
          onClick={handleRequestEndorsement}
          loading={createMessageLoading}
        >
          {t("Solicitar Validación")}
        </Button>
      </div>
    </div>
  );
};

export default RequestEndorsement;
